// WAAP Advanced Protection — account-level strategy rules (vendor
// "waf_advanced_strategy"). One vendor surface backs several tabs, each a
// different `type` with its own `config` shape:
//
//   type 1 → Anti-Leech            type 2 → Brute Force Protection
//
// CONFIG-AGNOSTIC: each tab's form owns its per-type `config` object; the BE
// (eagle_waf/advanced) passes it through, injecting the common envelope keys
// (name/comment/sites). So this module is generic — `config` is an opaque object.
// Backed by the PRIVATE portal (waap2). Account-level: the `:id` domain only
// resolves the customer session; a rule is applied to sites via "Apply to". Hooks
// take an ANCHOR domain UUID (any account website):
//
//   GET    /waap/domains/:id/advanced-strategies?type=N   → portal GET    /waf_advanced_strategies?type=N
//   POST   /waap/domains/:id/advanced-strategies          → portal POST   /waf_advanced_strategy
//   GET    /waap/domains/:id/advanced-strategies/:sid     → portal GET    /waf_advanced_strategy/:sid
//   PUT    /waap/domains/:id/advanced-strategies/:sid     → portal PUT    /waf_advanced_strategy (id in body)
//   DELETE /waap/domains/:id/advanced-strategies/:sid     → portal DELETE /waf_advanced_strategy/:sid
//   GET    /waap/domains/:id/advanced-enabled-sites       → portal GET    /sites/enabled

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

// ---- Types ----------------------------------------------------------------

/** Advanced-Protection rule `type`. */
export const ADVANCED_TYPE_ANTI_LEECH = 1;
export const ADVANCED_TYPE_BRUTE_FORCE = 2;
export const ADVANCED_TYPE_CC_PROTECTION = 4;
export const ADVANCED_TYPE_XML_PROTECTION = 5;
export const ADVANCED_TYPE_WEAK_PASSWORD = 6;
export const ADVANCED_TYPE_VULNERABILITY_SCANNING = 7;
export const ADVANCED_TYPE_HONEYPOT = 9;
export const ADVANCED_TYPE_COOKIE = 10;
export const ADVANCED_TYPE_ILLEGAL_DOWNLOAD = 11;

/** An Advanced-Protection rule (create/update body + get-one). `config` is the
 *  per-type config object — the owning form defines its shape. `id` absent when
 *  creating. */
export interface AdvancedStrategy<TConfig = Record<string, unknown>> {
  id?: number;
  type: number;
  name: string;
  description: string;
  /** Site IDs this rule applies to (AdvancedEnabledSite.id). */
  sites: number[];
  config: TConfig;
}

/** A row in the Advanced-Protection rule list. */
export interface AdvancedStrategyListItem {
  id: number;
  type: number;
  name: string;
  description: string;
  /** Site IDs the rule applies to (resolved to names via enabled-sites). */
  sites: number[];
}

/** An "enabled" site for the Apply-to picker (portal /sites/enabled). */
export interface AdvancedEnabledSite {
  id: number;
  domain: string;
  domain_id: number;
  protection: boolean;
  protectionMode: string;
  status: number;
  customer_id: number;
}

// ---- Paths + query keys --------------------------------------------------

function strategiesPath(domainId: string): string {
  return `/waap/domains/${domainId}/advanced-strategies`;
}
function enabledSitesPath(domainId: string): string {
  return `/waap/domains/${domainId}/advanced-enabled-sites`;
}

export const advancedKeys = {
  all: ['waap', 'advanced'] as const,
  strategies: (domainId: string, type: number) =>
    ['waap', 'advanced', domainId, 'strategies', type] as const,
  strategy: (domainId: string, type: number, id: string | number) =>
    ['waap', 'advanced', domainId, 'strategies', type, String(id)] as const,
  enabledSites: (domainId: string) => ['waap', 'advanced', domainId, 'enabled-sites'] as const,
};

// ---- Hooks ---------------------------------------------------------------

/** List the account's advanced-protection rules of a type (anchored on any domain UUID). */
export function useAdvancedStrategies(domainId: string | undefined, type: number) {
  return useQuery({
    queryKey: advancedKeys.strategies(domainId ?? '', type),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<AdvancedStrategyListItem[]> => {
      const res = await serviceApiV3.get<
        AdvancedStrategyListItem[] | { data: AdvancedStrategyListItem[] }
      >(strategiesPath(domainId as string), { params: { type } });
      const body = res.data as AdvancedStrategyListItem[] | { data?: AdvancedStrategyListItem[] };
      return Array.isArray(body) ? body : body?.data ?? [];
    },
  });
}

/** Fetch one advanced-protection rule (for the edit form). */
export function useAdvancedStrategy<TConfig = Record<string, unknown>>(
  domainId: string | undefined,
  type: number,
  id: string | undefined,
) {
  return useQuery({
    queryKey: advancedKeys.strategy(domainId ?? '', type, id ?? ''),
    enabled: !!domainId && !!id,
    retry: false,
    queryFn: async (): Promise<AdvancedStrategy<TConfig>> => {
      const res = await serviceApiV3.get<
        AdvancedStrategy<TConfig> | { data: AdvancedStrategy<TConfig> }
      >(`${strategiesPath(domainId as string)}/${id}`, { params: { type } });
      const body = res.data as AdvancedStrategy<TConfig> | { data?: AdvancedStrategy<TConfig> };
      return (
        (body as { data?: AdvancedStrategy<TConfig> })?.data ?? (body as AdvancedStrategy<TConfig>)
      );
    },
  });
}

/** Enabled sites for the Apply-to picker (anchored on any account domain UUID). */
export function useAdvancedEnabledSites(domainId: string | undefined) {
  return useQuery({
    queryKey: advancedKeys.enabledSites(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<AdvancedEnabledSite[]> => {
      const res = await serviceApiV3.get<AdvancedEnabledSite[] | { data: AdvancedEnabledSite[] }>(
        enabledSitesPath(domainId as string),
      );
      const body = res.data as AdvancedEnabledSite[] | { data?: AdvancedEnabledSite[] };
      return Array.isArray(body) ? body : body?.data ?? [];
    },
  });
}

function invalidateStrategies(qc: ReturnType<typeof useQueryClient>, domainId: string, type: number) {
  qc.invalidateQueries({ queryKey: advancedKeys.strategies(domainId, type) });
}

/** Create an advanced-protection rule. */
export function useCreateAdvancedStrategy<TConfig = Record<string, unknown>>(
  domainId: string,
  type: number,
  options?: UseMutationOptions<unknown, unknown, AdvancedStrategy<TConfig>>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AdvancedStrategy<TConfig>) => {
      const res = await serviceApiV3.post(strategiesPath(domainId), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidateStrategies(qc, domainId, type);
      options?.onSuccess?.(...args);
    },
  });
}

/** Update an advanced-protection rule. */
export function useUpdateAdvancedStrategy<TConfig = Record<string, unknown>>(
  domainId: string,
  type: number,
  id: number | string,
  options?: UseMutationOptions<unknown, unknown, AdvancedStrategy<TConfig>>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AdvancedStrategy<TConfig>) => {
      const res = await serviceApiV3.put(`${strategiesPath(domainId)}/${id}`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidateStrategies(qc, domainId, type);
      qc.invalidateQueries({ queryKey: advancedKeys.strategy(domainId, type, id) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete an advanced-protection rule. */
export function useDeleteAdvancedStrategy(
  domainId: string,
  type: number,
  options?: UseMutationOptions<unknown, unknown, number | string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await serviceApiV3.delete(`${strategiesPath(domainId)}/${id}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidateStrategies(qc, domainId, type);
      options?.onSuccess?.(...args);
    },
  });
}

// ---- Service-scoped twins (account-level, work with zero websites) ----------
// These mirror the domain-anchored hooks above but build /services/${serviceId}/waap/...

function serviceStrategiesPath(serviceId: string): string {
  return `/services/${serviceId}/waap/advanced-strategies`;
}
function serviceEnabledSitesPath(serviceId: string): string {
  return `/services/${serviceId}/waap/advanced-enabled-sites`;
}

export const advancedServiceKeys = {
  strategies: (serviceId: string, type: number) =>
    ['waap', 'advanced', 'service', serviceId, 'strategies', type] as const,
  strategy: (serviceId: string, type: number, id: string | number) =>
    ['waap', 'advanced', 'service', serviceId, 'strategies', type, String(id)] as const,
  enabledSites: (serviceId: string) =>
    ['waap', 'advanced', 'service', serviceId, 'enabled-sites'] as const,
};

/** List advanced-protection rules of a type, resolved from the service. */
export function useServiceAdvancedStrategies(serviceId: string | undefined, type: number) {
  return useQuery({
    queryKey: advancedServiceKeys.strategies(serviceId ?? '', type),
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<AdvancedStrategyListItem[]> => {
      const res = await serviceApiV3.get<
        AdvancedStrategyListItem[] | { data: AdvancedStrategyListItem[] }
      >(serviceStrategiesPath(serviceId as string), { params: { type } });
      const body = res.data as AdvancedStrategyListItem[] | { data?: AdvancedStrategyListItem[] };
      return Array.isArray(body) ? body : body?.data ?? [];
    },
  });
}

/** Fetch one advanced-protection rule, resolved from the service. */
export function useServiceAdvancedStrategy<TConfig = Record<string, unknown>>(
  serviceId: string | undefined,
  type: number,
  id: string | undefined,
) {
  return useQuery({
    queryKey: advancedServiceKeys.strategy(serviceId ?? '', type, id ?? ''),
    enabled: !!serviceId && !!id,
    retry: false,
    queryFn: async (): Promise<AdvancedStrategy<TConfig>> => {
      const res = await serviceApiV3.get<
        AdvancedStrategy<TConfig> | { data: AdvancedStrategy<TConfig> }
      >(`${serviceStrategiesPath(serviceId as string)}/${id}`, { params: { type } });
      const body = res.data as AdvancedStrategy<TConfig> | { data?: AdvancedStrategy<TConfig> };
      return (
        (body as { data?: AdvancedStrategy<TConfig> })?.data ?? (body as AdvancedStrategy<TConfig>)
      );
    },
  });
}

/** Enabled sites for the Apply-to picker, resolved from the service. */
export function useServiceAdvancedEnabledSites(serviceId: string | undefined) {
  return useQuery({
    queryKey: advancedServiceKeys.enabledSites(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<AdvancedEnabledSite[]> => {
      const res = await serviceApiV3.get<AdvancedEnabledSite[] | { data: AdvancedEnabledSite[] }>(
        serviceEnabledSitesPath(serviceId as string),
      );
      const body = res.data as AdvancedEnabledSite[] | { data?: AdvancedEnabledSite[] };
      return Array.isArray(body) ? body : body?.data ?? [];
    },
  });
}

function invalidateServiceStrategies(
  qc: ReturnType<typeof useQueryClient>,
  serviceId: string,
  type: number,
) {
  qc.invalidateQueries({ queryKey: advancedServiceKeys.strategies(serviceId, type) });
}

/** Create an advanced-protection rule, service-scoped. */
export function useCreateServiceAdvancedStrategy<TConfig = Record<string, unknown>>(
  serviceId: string,
  type: number,
  options?: UseMutationOptions<unknown, unknown, AdvancedStrategy<TConfig>>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AdvancedStrategy<TConfig>) => {
      const res = await serviceApiV3.post(serviceStrategiesPath(serviceId), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidateServiceStrategies(qc, serviceId, type);
      options?.onSuccess?.(...args);
    },
  });
}

/** Update an advanced-protection rule, service-scoped. */
export function useUpdateServiceAdvancedStrategy<TConfig = Record<string, unknown>>(
  serviceId: string,
  type: number,
  id: number | string,
  options?: UseMutationOptions<unknown, unknown, AdvancedStrategy<TConfig>>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AdvancedStrategy<TConfig>) => {
      const res = await serviceApiV3.put(`${serviceStrategiesPath(serviceId)}/${id}`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidateServiceStrategies(qc, serviceId, type);
      qc.invalidateQueries({ queryKey: advancedServiceKeys.strategy(serviceId, type, id) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete an advanced-protection rule, service-scoped. */
export function useDeleteServiceAdvancedStrategy(
  serviceId: string,
  type: number,
  options?: UseMutationOptions<unknown, unknown, number | string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await serviceApiV3.delete(`${serviceStrategiesPath(serviceId)}/${id}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidateServiceStrategies(qc, serviceId, type);
      options?.onSuccess?.(...args);
    },
  });
}
